import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

/**
 * Captures a DOM element and saves it as a PDF download.
 * @param {HTMLElement} element  - The poster DOM node to capture
 * @param {string}      filename - Output filename (without .pdf)
 * @param {Function}    onStart  - Called when generation begins
 * @param {Function}    onDone   - Called when PDF is saved
 */
export async function generatePDF(element, filename = 'itinerary', onStart, onDone) {
  try {
    if (onStart) onStart();

    const canvas = await html2canvas(element, {
      scale: 2,                  // 2× for crisp print quality
      useCORS: true,             // allow Unsplash images
      allowTaint: false,
      logging: false,
      backgroundColor: '#ffffff',
      windowWidth: element.scrollWidth,
      windowHeight: element.scrollHeight,
    });

    const imgData = canvas.toDataURL('image/jpeg', 0.95);

    /* A4 dimensions in mm */
    const pdfW = 210;
    const pdfH = Math.round((canvas.height * pdfW) / canvas.width);

    const pdf = new jsPDF({
      orientation: pdfH > pdfW ? 'portrait' : 'landscape',
      unit: 'mm',
      format: [pdfW, pdfH],
    });

    pdf.addImage(imgData, 'JPEG', 0, 0, pdfW, pdfH, undefined, 'FAST');
    pdf.save(`${filename}.pdf`);

    if (onDone) onDone();
  } catch (err) {
    console.error('PDF generation failed:', err);
    if (onDone) onDone();
    throw err;
  }
}

/**
 * Captures an array of DOM elements (one per page) and saves as a multi-page A4 PDF.
 * @param {HTMLElement[]} pageElements - One element per page
 * @param {string}        filename     - Output filename (without .pdf)
 */
export async function generateMultiPagePDF(pageElements, filename = 'document') {
  const pdfW = 210; // A4 mm
  const pdfH = 297;

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  for (let i = 0; i < pageElements.length; i++) {
    if (i > 0) pdf.addPage();

    const el = pageElements[i];
    const canvas = await html2canvas(el, {
      scale: 2,
      useCORS: true,
      allowTaint: false,
      logging: false,
      backgroundColor: '#ffffff',
      windowWidth: el.scrollWidth,
      windowHeight: el.scrollHeight,
    });

    const imgData = canvas.toDataURL('image/jpeg', 0.95);
    const imgH = Math.round((canvas.height * pdfW) / canvas.width);
    // If a single page block is taller than A4 (content overflow), scale to fit
    const drawH = Math.min(imgH, pdfH);
    pdf.addImage(imgData, 'JPEG', 0, 0, pdfW, drawH, undefined, 'FAST');
  }

  pdf.save(`${filename}.pdf`);
}

// Convert an image URL to base64 via fetch (works for same-origin + CORS URLs)
async function urlToBase64(url) {
  if (!url) return null;
  try {
    const res = await fetch(url, { mode: 'cors', cache: 'no-cache' });
    if (!res.ok) return null;
    const blob = await res.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror  = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    // Fallback: draw to canvas
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const c = document.createElement('canvas');
          c.width  = img.naturalWidth  || img.width;
          c.height = img.naturalHeight || img.height;
          c.getContext('2d').drawImage(img, 0, 0);
          resolve(c.toDataURL('image/jpeg', 0.85));
        } catch { resolve(null); }
      };
      img.onerror = () => resolve(null);
      img.src = url;
    });
  }
}

// ── Colour palette ────────────────────────────────────────────
const DARK  = [31,  58,  45];
const DGRAY = [20,  42,  32];
const GREEN = [45, 106,  80];
const GOLD  = [212,164,  55];
const LGREE = [240,250, 245];
const MUTED = [75,  85,  99];
const WHITE = [255,255, 255];
const BORD  = [198,232, 212];
const FTEXT = [180,220, 195];

// A4 dimensions + margins
const W = 210, H = 297, ML = 14, CW = 182;

// ── Section title with gold left bar ─────────────────────────
function secTitle(doc, text, y) {
  doc.setFillColor(...GOLD);
  doc.rect(ML, y - 5.5, 2.5, 7.5, 'F');
  doc.setTextColor(...GREEN);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text(text.toUpperCase(), ML + 6, y);
}

// ── Compact header (pages 2+) ─────────────────────────────────
function miniHeader(doc, company, logoB64, destName) {
  doc.setFillColor(...DARK);
  doc.rect(0, 0, W, 22, 'F');
  if (logoB64) { try { doc.addImage(logoB64, 'JPEG', ML, 3, 16, 16); } catch (_) {} }
  doc.setTextColor(...WHITE);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(company.name, ML + 20, 12);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.setTextColor(...GOLD);
  doc.text(company.tagline.toUpperCase(), ML + 20, 18);
  doc.setTextColor(200, 230, 215);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.text(`${destName} — Itinerary`, W - ML, 14, { align: 'right' });
  doc.setFillColor(...GOLD);
  doc.rect(0, 22, W, 1.5, 'F');
}

// ── Footer with full contact details ─────────────────────────
function pageFooter(doc, company, logoB64) {
  const fy = H - 20;
  doc.setFillColor(...DARK);
  doc.rect(0, fy, W, 20, 'F');
  doc.setFillColor(...GOLD);
  doc.rect(0, fy, W, 0.8, 'F');
  if (logoB64) { try { doc.addImage(logoB64, 'JPEG', ML, fy + 3, 13, 13); } catch (_) {} }
  doc.setTextColor(...WHITE);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text(company.name, ML + 17, fy + 9);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6);
  doc.setTextColor(...GOLD);
  doc.text(company.tagline, ML + 17, fy + 14);
  doc.setTextColor(...FTEXT);
  doc.setFontSize(7);
  doc.text(`Ph: ${company.phone}  |  ${company.altPhone}`, W / 2, fy + 6.5,  { align: 'center' });
  doc.text(`Email: ${company.email}  |  Web: ${company.website}`,  W / 2, fy + 11.5, { align: 'center' });
  doc.text(`Address: ${company.address}`, W / 2, fy + 16.5, { align: 'center' });
}

// ── Main export ───────────────────────────────────────────────
export async function generateItineraryPDF(data, logoSrc, company, filename = 'Itinerary', onStart, onDone) {
  if (onStart) onStart();
  try {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

    // Pre-load all images in parallel
    const [logoB64, heroB64, ...extraB64] = await Promise.all([
      urlToBase64(logoSrc),
      urlToBase64(data.images?.[0]),
      ...((data.images || []).slice(1, 4).map(u => urlToBase64(u))),
    ]);

    // ══════════════════════════════════════════════════════════
    // PAGE 1 — Cover
    // ══════════════════════════════════════════════════════════

    // Header bar
    doc.setFillColor(...DARK);
    doc.rect(0, 0, W, 28, 'F');
    if (logoB64) { try { doc.addImage(logoB64, 'JPEG', ML, 4, 20, 20); } catch (_) {} }
    doc.setTextColor(...WHITE);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(company.name, ML + 26, 15);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...GOLD);
    doc.text(company.tagline.toUpperCase(), ML + 26, 21);
    // "TRAVEL ITINERARY" badge
    doc.setFillColor(...GOLD);
    doc.roundedRect(W - ML - 38, 9, 38, 12, 2, 2, 'F');
    doc.setTextColor(...DARK);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text('TRAVEL ITINERARY', W - ML - 19, 16.5, { align: 'center' });

    // 4-colour accent stripe
    [[31,58,45],[212,164,55],[124,58,29],[62,134,107]].forEach(([r,g,b], i) => {
      doc.setFillColor(r, g, b);
      doc.rect(i * (W / 4), 28, W / 4, 3, 'F');
    });

    // Hero image
    if (heroB64) { try { doc.addImage(heroB64, 'JPEG', 0, 31, W, 58); } catch (_) {} }
    else { doc.setFillColor(...DARK); doc.rect(0, 31, W, 58, 'F'); }

    // Destination nameplate (dark bg below image)
    doc.setFillColor(...DGRAY);
    doc.rect(0, 89, W, 28, 'F');
    doc.setTextColor(...WHITE);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(28);
    doc.text(data.destination || '', ML, 108);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(11);
    doc.setTextColor(200, 235, 215);
    doc.text(data.tagline || '', ML, 115);

    // Region pill
    doc.setFillColor(...GOLD);
    doc.roundedRect(W - ML - 32, 91, 32, 8, 2, 2, 'F');
    doc.setTextColor(...DARK);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.text((data.region || '').toUpperCase(), W - ML - 16, 96.5, { align: 'center' });

    // Price box
    doc.setFillColor(...GOLD);
    doc.roundedRect(W - ML - 38, 100, 38, 14, 2, 2, 'F');
    doc.setTextColor(...DARK);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.text('STARTING FROM', W - ML - 19, 105.5, { align: 'center' });
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text(data.priceFrom || 'On Request', W - ML - 19, 112, { align: 'center' });

    // Meta strip
    doc.setFillColor(38, 68, 52);
    doc.rect(0, 117, W, 18, 'F');
    [
      { label: 'Duration',      val: data.duration  || '-' },
      { label: 'Hotel',         val: data.hotel     || '-' },
      { label: 'Meals',         val: data.meals     || '-' },
      { label: 'Best Time',     val: data.bestTime  || '-' },
    ].forEach((m, i) => {
      const cx = i * (W / 4) + W / 8;
      doc.setTextColor(150, 200, 175);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(5.5);
      doc.text(m.label.toUpperCase(), cx, 122.5, { align: 'center' });
      doc.setTextColor(...WHITE);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.text(doc.splitTextToSize(m.val, W / 4 - 4)[0], cx, 129.5, { align: 'center' });
      if (i < 3) {
        doc.setDrawColor(...WHITE);
        doc.setLineWidth(0.15);
        doc.line((i + 1) * (W / 4), 119, (i + 1) * (W / 4), 133);
      }
    });

    // ── body
    let y = 145;
    const bottomLimit = H - 24;

    // Overview
    secTitle(doc, 'Trip Overview', y);
    y += 6;
    doc.setTextColor(...MUTED);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const oLines = doc.splitTextToSize(data.overview || '', CW);
    doc.text(oLines, ML, y);
    y += oLines.length * 4.5 + 8;

    // Highlights
    if (y + 20 < bottomLimit && (data.highlights || []).length > 0) {
      secTitle(doc, 'Trip Highlights', y);
      y += 6;
      const chipW = (CW - 6) / 3;
      data.highlights.forEach((h, i) => {
        const col = i % 3;
        if (col === 0 && i > 0) y += 9;
        if (y + 9 >= bottomLimit) return;
        const cx = ML + col * (chipW + 3);
        doc.setFillColor(...LGREE);
        doc.setDrawColor(...BORD);
        doc.setLineWidth(0.25);
        doc.roundedRect(cx, y - 4, chipW, 7.5, 2, 2, 'FD');
        doc.setTextColor(...DARK);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.5);
        doc.text(doc.splitTextToSize('+ ' + h, chipW - 4)[0], cx + 3, y + 0.5);
      });
      y += 10;
    }

    // Photo gallery strip
    const validExtras = extraB64.filter(Boolean);
    if (validExtras.length > 0 && y + 32 < bottomLimit) {
      secTitle(doc, 'Photo Gallery', y);
      y += 6;
      const gw = (CW / validExtras.length) - 2;
      validExtras.forEach((img, i) => {
        try { doc.addImage(img, 'JPEG', ML + i * (gw + 2), y, gw, 26); } catch (_) {}
      });
    }

    pageFooter(doc, company, logoB64);

    // ══════════════════════════════════════════════════════════
    // PAGE 2+ — Day-Wise Itinerary
    // ══════════════════════════════════════════════════════════
    if ((data.days || []).length > 0) {
      doc.addPage();
      miniHeader(doc, company, logoB64, data.destination);
      y = 32;
      secTitle(doc, 'Day-Wise Itinerary', y);
      y += 8;

      for (const d of data.days) {
        const actLines = doc.splitTextToSize(
          (d.activities || []).join('  ·  '),
          CW - 36
        );
        const rh = Math.max(15, 11 + actLines.length * 4.2);

        if (y + rh > H - 24) {
          pageFooter(doc, company, logoB64);
          doc.addPage();
          miniHeader(doc, company, logoB64, data.destination);
          y = 32;
        }

        // Alternating row background
        doc.setFillColor(d.day % 2 === 0 ? 248 : 244, d.day % 2 === 0 ? 252 : 249, d.day % 2 === 0 ? 249 : 247);
        doc.setDrawColor(...BORD);
        doc.setLineWidth(0.25);
        doc.roundedRect(ML, y, CW, rh, 2, 2, 'FD');
        // Left accent
        doc.setFillColor(...DARK);
        doc.roundedRect(ML, y, 3, rh, 1, 1, 'F');
        // Day badge
        doc.setFillColor(...DARK);
        doc.roundedRect(ML + 6, y + 2.5, 18, 7, 2, 2, 'F');
        doc.setTextColor(...GOLD);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.text(`DAY ${d.day}`, ML + 15, y + 7.8, { align: 'center' });
        // Title
        doc.setTextColor(...DARK);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9.5);
        doc.text(d.title || '', ML + 29, y + 8);
        // Activities
        doc.setTextColor(...MUTED);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.5);
        doc.text(actLines, ML + 29, y + 13.5);
        y += rh + 3;
      }

      pageFooter(doc, company, logoB64);
    }

    // ══════════════════════════════════════════════════════════
    // PAGE 3 — Package Inclusions / Exclusions + Contact
    // ══════════════════════════════════════════════════════════
    doc.addPage();
    miniHeader(doc, company, logoB64, data.destination);
    y = 32;

    const incList = data.inclusions || [];
    const excList = data.exclusions || [];

    if (incList.length > 0 || excList.length > 0) {
      secTitle(doc, 'Package Inclusions & Exclusions', y);
      y += 8;

      const colW = (CW - 8) / 2;

      // Inclusions box
      const incH = 12 + incList.length * 6 + 2;
      doc.setFillColor(240, 250, 245);
      doc.setDrawColor(...BORD);
      doc.setLineWidth(0.4);
      doc.roundedRect(ML, y, colW, incH, 3, 3, 'FD');
      doc.setFillColor(31, 110, 68);
      doc.roundedRect(ML, y, colW, 10, 3, 3, 'F');
      doc.rect(ML, y + 5, colW, 5, 'F');
      doc.setTextColor(...WHITE);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text('INCLUDED IN PACKAGE', ML + colW / 2, y + 7, { align: 'center' });
      incList.forEach((item, ii) => {
        doc.setTextColor(31, 90, 55);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.text(`+  ${item}`, ML + 6, y + 16 + ii * 6);
        if (ii < incList.length - 1) {
          doc.setDrawColor(...BORD);
          doc.setLineWidth(0.15);
          doc.line(ML + 5, y + 18 + ii * 6, ML + colW - 5, y + 18 + ii * 6);
        }
      });

      // Exclusions box
      const excH = 12 + excList.length * 6 + 2;
      const excX = ML + colW + 8;
      doc.setFillColor(255, 245, 245);
      doc.setDrawColor(252, 200, 200);
      doc.setLineWidth(0.4);
      doc.roundedRect(excX, y, colW, excH, 3, 3, 'FD');
      doc.setFillColor(192, 57, 43);
      doc.roundedRect(excX, y, colW, 10, 3, 3, 'F');
      doc.rect(excX, y + 5, colW, 5, 'F');
      doc.setTextColor(...WHITE);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text('NOT INCLUDED', excX + colW / 2, y + 7, { align: 'center' });
      excList.forEach((item, ii) => {
        doc.setTextColor(139, 34, 34);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.text(`-  ${item}`, excX + 6, y + 16 + ii * 6);
        if (ii < excList.length - 1) {
          doc.setDrawColor(252, 200, 200);
          doc.setLineWidth(0.15);
          doc.line(excX + 5, y + 18 + ii * 6, excX + colW - 5, y + 18 + ii * 6);
        }
      });

      y += Math.max(incH, excH) + 14;
    }

    // Contact card
    if (y < H - 68) {
      secTitle(doc, 'Contact Us to Book Your Trip', y);
      y += 8;

      doc.setFillColor(...DGRAY);
      doc.roundedRect(ML, y, CW, 52, 4, 4, 'F');

      // Logo in card
      if (logoB64) { try { doc.addImage(logoB64, 'JPEG', ML + 7, y + 10, 26, 26); } catch (_) {} }

      // Company name
      doc.setTextColor(...WHITE);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.text(company.name, ML + 40, y + 17);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(...GOLD);
      doc.text(company.tagline, ML + 40, y + 24);

      // Divider line
      doc.setFillColor(...GOLD);
      doc.rect(ML + 40, y + 27, CW - 46, 0.6, 'F');

      // Contact details in grid
      const cDetails = [
        ['Phone',    company.phone    ],
        ['Alternate', company.altPhone ],
        ['Email',    company.email    ],
        ['Website',  company.website  ],
        ['Address',  company.address  ],
      ];
      cDetails.forEach(([label, val], ci) => {
        const col = ci < 4 ? ci % 2 : 0;
        const row = ci < 4 ? Math.floor(ci / 2) : 2;
        const dx = ML + 40 + col * 74;
        const dy = y + 32 + row * 10;
        doc.setTextColor(130, 185, 160);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6);
        doc.text(label.toUpperCase(), dx, dy);
        doc.setTextColor(...WHITE);
        doc.setFontSize(8);
        doc.text(val || '', dx, dy + 5.5);
      });
    }

    pageFooter(doc, company, logoB64);

    doc.save(`${filename}.pdf`);
  } finally {
    if (onDone) onDone();
  }
}
